import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { BackgroundImage } from '../components/BackgroundImage'
import { CustomPopup, usePopup } from '../components/CustomPopup'
import { generateSolutionUrl } from '../utils/solutionUrls'
import { useConversionTracking } from '../hooks/useRevenueTracking'
import { fetchAuthSession } from 'aws-amplify/auth'

export function SolutionDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuth()
  const { popup, showPopup, closePopup } = usePopup()
  const { trackUpgradePromptShown, trackUpgradePromptClicked, trackSolutionViewed } = useConversionTracking()
  const [isProcessing, setIsProcessing] = useState(false)
  const [hasAccess, setHasAccess] = useState(false)
  const [solutionToken, setSolutionToken] = useState<string | null>(null)
  const [solution, setSolution] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [userTier, setUserTier] = useState<'free' | 'registered' | 'pro'>('free')

  // Fetch solution data from API
  useEffect(() => {
    const fetchSolution = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL.replace(/\/$/, "")}/catalog/${id}`)
        if (response.ok) {
          const data = await response.json()
          setSolution(data.solution)
          
          // Track solution view
          trackSolutionViewed(data.solution.solutionId, data.solution.name)
        } else {
          console.error('Failed to fetch solution')
        }
      } catch (error) {
        console.error('Error fetching solution:', error)
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchSolution()
    }
  }, [id, trackSolutionViewed])

  // Check if user has access to this solution
  useEffect(() => {
    const checkAccess = async () => {
      if (!isAuthenticated || !user) return

      try {
        // Get user email from user object or localStorage
        const userEmail = user.email || localStorage.getItem('userEmail') || localStorage.getItem('email')
        
        if (!userEmail) {
          console.error('No user email available')
          return
        }

        // Get user's subscriptions to find the token
        const response = await fetch(`${import.meta.env.VITE_API_URL.replace(/\/$/, "")}/api/subscriptions`, {
          headers: {
            'x-user-email': userEmail
          }
        })

        if (response.ok) {
          const data = await response.json()
          const subscription = data.subscriptions?.find((sub: any) => sub.solutionId === (solution?.solutionId || id))
          
          if (subscription && subscription.status === 'active') {
            setHasAccess(true)
            setSolutionToken(subscription.token)
          }
        } else {
          console.error('Subscriptions API error:', response.status, await response.text())
        }
      } catch (error) {
        console.error('Error checking solution access:', error)
      }
    }

    if (solution?.solutionId) {
      checkAccess()
    }
  }, [isAuthenticated, user, solution?.solutionId])

  // Check user tier for AWS Solution Finder
  useEffect(() => {
    const checkUserTier = async () => {
      if (!isAuthenticated || !user || solution?.solutionId !== 'aws-solution-finder-001') return

      try {
        // Get fresh token from Amplify session
        const session = await fetchAuthSession()
        const token = session.tokens?.idToken?.toString()
        
        if (!token) {
          console.error('No auth token available')
          return
        }

        const response = await fetch(`${import.meta.env.VITE_API_URL.replace(/\/$/, "")}/user/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (response.ok) {
          const data = await response.json()
          const entitlements = data.entitlements || []
          const awsFinderEnt = entitlements.find((e: any) => 
            e.solutionId === 'aws-solution-finder' || e.solutionId === 'aws-solution-finder-001'
          )
          
          if (awsFinderEnt) {
            const tier = awsFinderEnt.tier || awsFinderEnt.access_tier || 'registered'
            if (tier === 'pro' && awsFinderEnt.pro_expires_at) {
              const expiryDate = new Date(awsFinderEnt.pro_expires_at)
              setUserTier(expiryDate > new Date() ? 'pro' : 'registered')
            } else {
              setUserTier(tier as any)
            }
          } else {
            setUserTier('registered')
          }
        }
      } catch (error) {
        console.error('Error checking user tier:', error)
      }
    }

    checkUserTier()
  }, [isAuthenticated, user, solution?.solutionId])

  const handleAccessSolution = async () => {
    console.log('🎯 handleAccessSolution called')
    console.log('  hasAccess:', hasAccess)
    console.log('  solutionToken:', solutionToken)
    console.log('  user:', user)
    console.log('  userTier:', userTier)
    console.log('  solution?.solutionId:', solution?.solutionId)
    
    // If hasAccess but no token, fetch it now (race condition fix)
    if (hasAccess && !solutionToken && user && solution?.solutionId) {
      console.log('🔄 Token missing, fetching now...')
      try {
        const userEmail = user.email || localStorage.getItem('userEmail') || localStorage.getItem('email')
        if (!userEmail) {
          console.error('❌ No user email available')
          return
        }
        const response = await fetch(`${import.meta.env.VITE_API_URL.replace(/\/$/, "")}/api/subscriptions`, {
          headers: { 'x-user-email': userEmail }
        })
        if (response.ok) {
          const data = await response.json()
          const subscription = data.subscriptions?.find((sub: any) => sub.solutionId === solution.solutionId)
          if (subscription?.token) {
            const solutionUrl = generateSolutionUrl({
              solutionId: solution.solutionId,
              token: subscription.token,
              userEmail: user.email,
              tier: userTier || subscription.tier || 'registered'
            })
            console.log('🚀 Opening solution with fetched token')
            window.open(solutionUrl, '_blank')
            return
          }
        }
      } catch (error) {
        console.error('❌ Error fetching token:', error)
      }
    }
    
    // If user doesn't have access yet, try to create free tier entitlement
    if (!hasAccess && user && solution?.solutionId) {
      console.log('🔄 No access yet, attempting to create free tier entitlement...')
      
      try {
        setIsProcessing(true)
        
        // Call generate-solution-token API to create entitlement
        const response = await fetch(`${import.meta.env.VITE_API_URL.replace(/\/$/, "")}/api/generate-solution-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({
            user_id: user.email, // Use email as user_id
            solution_id: solution.solutionId,
            tier: 'registered'
          })
        })

        if (response.ok) {
          const data = await response.json()
          console.log('✅ Entitlement created:', data)
          
          // Update state with new token
          setSolutionToken(data.token)
          setHasAccess(true)
          
          // Now open the solution
          const solutionUrl = generateSolutionUrl({
            solutionId: solution.solutionId,
            token: data.token,
            userEmail: user.email,
            tier: 'registered'
          })
          console.log('🚀 Opening solution with URL:', solutionUrl)
          window.open(solutionUrl, '_blank')
        } else {
          const error = await response.json()
          console.error('❌ Failed to create entitlement:', error)
          showPopup('Failed to access solution. Please try again.', 'error')
        }
      } catch (error) {
        console.error('❌ Error creating entitlement:', error)
        showPopup('Failed to access solution. Please try again.', 'error')
      } finally {
        setIsProcessing(false)
      }
      return
    }
    
    // If user already has access, just open the solution
    if (hasAccess && solutionToken && user && solution?.solutionId) {
      const solutionUrl = generateSolutionUrl({
        solutionId: solution.solutionId,
        token: solutionToken,
        userEmail: user.email,
        tier: userTier || 'registered'
      })
      console.log('🚀 Opening solution with URL:', solutionUrl)
      window.open(solutionUrl, '_blank')
    } else {
      console.error('❌ Cannot launch - missing required data')
    }
  }

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: `/solutions/${id}` } } })
      return
    }

    setIsProcessing(true)
    
    try {
      // Create payment order
      const response = await fetch(`${import.meta.env.VITE_API_URL.replace(/\/$/, "")}/payments/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          solutionId: id,
          amount: solution.price,
          currency: solution.currency,
          userId: user?.userId,
          userEmail: user?.email,
          userName: user?.profile.name
        })
      })

      const data = await response.json()
      
      if (data.success && data.redirectUrl) {
        // Redirect to PhonePe payment page
        window.location.href = data.redirectUrl
      } else {
        throw new Error(data.message || 'Payment initiation failed')
      }
    } catch (error) {
      console.error('Payment error:', error)
      showPopup('Payment initiation failed. Please try again.', 'error')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className="min-h-screen">
      <BackgroundImage />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading solution details...</div>
        </div>
      ) : !solution ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Solution not found</div>
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Solution Image */}
        <div className="bg-gray-200 rounded-lg flex items-center justify-center min-h-[300px]">
          {solution.imageUrl ? (
            <img 
              src={solution.imageUrl} 
              alt={solution.name} 
              className="w-full h-auto rounded-lg"
            />
          ) : (
            <span className="text-gray-500">Solution Preview</span>
          )}
        </div>

        {/* Solution Details */}
        <div className="space-y-6">
          <div>
            <div className="flex items-start justify-between">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                {(solution as any).icon && <span className="text-4xl">{(solution as any).icon}</span>}
                {solution.name}
              </h1>
              {(solution as any).currentVersion && (
                <span className="px-3 py-1 text-sm rounded-full bg-gray-100 text-gray-700 font-mono">
                  v{(solution as any).currentVersion}
                </span>
              )}
            </div>
            <p className="text-lg text-gray-600 mt-2">by CloudNestle Consulting & Services</p>
            <span className="inline-block bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full mt-2">
              {solution.category}
            </span>
          </div>

          <p className="text-gray-700">{solution.description}</p>

          {/* Product Information */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Product Information</h3>
            <div className="space-y-1 text-sm">
              {(solution as any).currentVersion && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Version:</span>
                  <span className="font-semibold text-gray-900 font-mono">{(solution as any).currentVersion}</span>
                </div>
              )}
              {(solution as any).lastUpdated && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="font-semibold text-gray-900">
                    {new Date((solution as any).lastUpdated).toLocaleDateString('en-US', { 
                      year: 'numeric', month: 'long', day: 'numeric' 
                    })}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Delivery Method:</span>
                <span className="font-semibold text-gray-900">Software as a Service (SaaS)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Deployed on AWS:</span>
                <span className="font-semibold text-gray-900">Yes</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Country of Origin:</span>
                <span className="font-semibold text-gray-900">🇮🇳 India</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Vendor:</span>
                <span className="font-semibold text-gray-900">CloudNestle Consulting & Services</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">GSTIN:</span>
                <span className="font-mono text-xs font-semibold text-gray-900">29ADWPA6289Q1ZB</span>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h3>
            <ul className="space-y-2">
              {solution.features?.map((feature: string, index: number) => (
                <li key={index} className="flex items-start text-gray-700">
                  <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Pricing */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Pricing</h3>
            <div className="space-y-3">
              {/* Free Tier */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-gray-900">Free Tier</div>
                  <div className="text-xs text-gray-600">10 searches per day</div>
                </div>
                <div className="text-xl font-bold text-green-600">$0/user/month</div>
              </div>
              
              {/* Pro Tier */}
              <div className="flex items-center justify-between border-t pt-3">
                <div>
                  <div className="font-semibold text-gray-900">Pro Tier Subscription</div>
                  <div className="text-xs text-gray-600">Unlimited searches</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-purple-600">${solution?.pricing?.pro?.displayPrice || 0}/user/month*</div>
                  <div className="text-xs text-gray-500 text-right mt-0.5">*Shown in your currency at checkout</div>
                </div>
              </div>
            </div>

            {/* GST Notice */}
            <div className="mt-3 pt-3 border-t text-xs text-gray-600">
              <div className="space-y-1">
                <p><span className="font-semibold">🇮🇳 Indian buyers:</span> +18% GST</p>
                <p><span className="font-semibold">🌐 International:</span> 0% GST (Charged in your local currency)</p>
              </div>
            </div>
          </div>

          {/* Coming Soon Notice */}
          {(solution as any).solutionStatus === 'coming-soon' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <div className="text-center">
                <div className="text-4xl mb-3">🚀</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Early Access</h3>
                <p className="text-sm text-gray-600 mb-4">
                  This solution is in early access and will be fully available soon.
                </p>
                {(solution as any).expectedLaunch && (
                  <p className="text-xs text-gray-500 mb-4">
                    Expected Launch: {new Date((solution as any).expectedLaunch).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </p>
                )}
                <button
                  onClick={() => {
                    showPopup('We\'ll notify you when this solution launches. Please check back soon!', 'info')
                  }}
                  className="w-full bg-amber-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-amber-700 transition-colors"
                >
                  🔔 Notify Me When Available
                </button>
              </div>
            </div>
          )}

          {/* Launch Options */}
          {(solution as any).solutionStatus !== 'coming-soon' && solution.solutionId === 'aws-solution-finder-001' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Launch Options</h3>
              <div className="space-y-3">
                {/* Launch Free Trial Button */}
                {!isAuthenticated && (
                  <button
                    onClick={() => window.open(`${import.meta.env.VITE_FAISS_BASE_URL || 'https://awssolutionfinder.solutions.cloudnestle.com'}/search`, '_blank')}
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    🚀 Try Free (3 searches/day)
                  </button>
                )}
                
                {isAuthenticated && userTier !== 'pro' && (
                  <button
                    onClick={handleAccessSolution}
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    🚀 Launch Free Trial (Maximum of 10 searches per day)
                  </button>
                )}
                
                {isAuthenticated && userTier === 'pro' && (
                  <button
                    onClick={handleAccessSolution}
                    className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                  >
                    ⭐ Launch Pro (Unlimited)
                  </button>
                )}
                
                {/* Upgrade to Pro Button */}
                {isAuthenticated && userTier !== 'pro' && (
                  <div>
                    <button
                      onClick={() => {
                        trackUpgradePromptClicked('solution_detail_page')
                        navigate('/upgrade?solution=aws-solution-finder-001')
                      }}
                      onMouseEnter={() => trackUpgradePromptShown('solution_detail_page')}
                      className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                    >
                      ⭐ Upgrade to Pro - ${solution?.pricing?.pro?.displayPrice || 0}/user/month
                    </button>
                    <p className="text-xs text-gray-500 text-center mt-2">Charged in your local currency at checkout.</p>
                  </div>
                )}
                
                {!isAuthenticated && (
                  <p className="text-sm text-gray-600 text-center">
                    <button onClick={() => navigate('/login')} className="text-blue-600 hover:underline">Login</button> for 10 searches/day or <button onClick={() => navigate('/register')} className="text-blue-600 hover:underline">Register</button> now
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Generic Solution Buttons */}
          {(solution as any).solutionStatus !== 'coming-soon' && solution.solutionId !== 'aws-solution-finder-001' && (
            <>
              {isAuthenticated && (
                <button
                  onClick={handleAccessSolution}
                  disabled={isProcessing}
                  className="w-full mt-6 bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    <span>{hasAccess ? 'Access Solution' : 'Access Now (Free)'}</span>
                  </button>
                )}

                {isAuthenticated && user?.role === 'customer' && solution.pricing?.amount > 0 && (
                  <button
                    onClick={handlePurchase}
                    disabled={isProcessing}
                    className="w-full mt-6 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isProcessing ? 'Processing...' : 'Upgrade to Pro'}
                  </button>
                )}

                {!isAuthenticated && (
                  <button
                    onClick={() => navigate('/login', { state: { from: { pathname: `/solutions/${id}` } } })}
                    className="w-full mt-6 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Login to Access
                  </button>
                )}
              </>
            )}
        </div>
      </div>
      )}
      </div>
      {popup && <CustomPopup {...popup} onClose={closePopup} />}
    </div>
  )
}